import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";

import i18n, { ensureI18nReady } from "@/lib/i18next";
import appCss from "../styles.css?url";
import * as Sentry from "@sentry/tanstackstart-react";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { BrandSplashOverlay } from "../components/BrandSplashOverlay";
import { TabBar } from "../components/TabBar";
import { SoftErrorBoundary } from "../components/SoftErrorBoundary";
import { LoginModal } from "../components/LoginModal";
import { DiscoveryTour } from "@/components/onboarding/DiscoveryTour";
import { Toaster } from "../components/ui/sonner";
import { useAuth, AuthProvider } from "../lib/auth";
import { LocationProvider, hasStoredCity, LOCATION_DETECTING_LABEL, useLocation } from "@/lib/location";
import { DEFAULT_LANGUAGE } from "../lib/settings";
import { activeLanguage } from "../lib/contentLanguage";
import { initTheme } from "@/lib/theme";
import { initFontGroup } from "@/lib/fontGroup";
import { registerServiceWorker } from "@/lib/pushNotifications";
import { applyStoredLanguage, CONSUMER_LANGUAGE_STORAGE_KEY } from "@/lib/languageStorage";
import { cn } from "@/lib/utils";
import { useSquadMembershipSync } from "@/hooks/useSquadMembershipSync";
import { useSquadRoleSync } from "@/hooks/useSquadRoleSync";
import { useRejoinRequestSync } from "@/hooks/useRejoinRequestSync";
import { useSquadInviteSync } from "@/hooks/useSquadInviteSync";
import { LoadingBlock } from "@/components/AsyncState";
import { SubscriptionRequiredScreen } from "@/components/account/SubscriptionRequiredScreen";
import { loadConsumerEntitlements, useConsumerEntitlements } from "@/lib/consumerEntitlements";
import { useConsumerOnboarding } from "@/lib/consumerOnboarding";
import { isAdminPath } from "@/lib/adminRoute";
import { isPublicMarketingPath } from "@/lib/launchMode";
import {
  getDiscoverySplashPrefetchPromise,
  getSplashPrefetchPhase,
  subscribeSplashPrefetchPhase,
  type SplashPrefetchPhase,
} from "@/lib/discoverySplashPrefetch";

function splashStatusKey(phase: SplashPrefetchPhase): string {
  switch (phase) {
    case "images":
      return "discovery.splash.loadingImages";
    case "capped":
    case "done":
      return "discovery.splash.almostReady";
    case "feed":
      return "discovery.splash.loadingSpots";
    default:
      return "discovery.splash.preparingNightlife";
  }
}

function NotFoundComponent() {
  const { t } = useTranslation("consumer");
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-glow" />
      <div className="relative max-w-md text-center">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Pulse</p>
        <h1 className="mt-2 text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t("root.notFoundTitle")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("root.notFoundMessage")}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-gradient-pulse px-6 py-3 text-sm font-bold text-pulse-foreground shadow-pulse"
          >
            {t("root.backToDiscover")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const { t } = useTranslation("consumer");
  const router = useRouter();
  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: "tanstack_root_error_component" } });
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-glow" />
      <div className="relative max-w-md text-center">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Pulse</p>
        <h1 className="mt-2 text-xl font-semibold tracking-tight text-foreground">
          {t("root.errorTitle")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("root.errorMessage")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-gradient-pulse px-6 py-3 text-sm font-bold text-pulse-foreground shadow-pulse"
          >
            {t("root.tryAgain")}
          </button>
          <Link
            to="/"
            className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground"
          >
            {t("root.backToDiscover")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function RouteOutletFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("consumer");
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <p className="text-sm font-semibold text-foreground">{t("root.errorTitle")}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{t("root.errorMessage")}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 inline-flex min-h-10 items-center justify-center rounded-2xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground"
      >
        {t("root.tryAgain")}
      </button>
    </div>
  );
}

function SoftRouteOutlet() {
  const [resetKey, setResetKey] = useState(0);
  return (
    <div className="pulse-vt-main">
      <SoftErrorBoundary
        key={resetKey}
        name="RouteOutlet"
        fallback={<RouteOutletFallback onRetry={() => setResetKey((n) => n + 1)} />}
      >
        <Outlet />
      </SoftErrorBoundary>
    </div>
  );
}

function SoftTabBar() {
  return (
    <SoftErrorBoundary name="TabBar" fallback={null}>
      <TabBar />
    </SoftErrorBoundary>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0A0A0A" },
      { title: "Pulse — Nightlife & Events" },
      { name: "description", content: "Discover bars, clubs, concerts and rooftops happening tonight in your city." },
      { property: "og:title", content: "Pulse — Nightlife & Events" },
      { property: "og:description", content: "Discover bars, clubs, concerts and rooftops happening tonight in your city." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        // Single combined stylesheet — marketing.css must not @import a second fonts.googleapis URL.
        href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500;600;700;800&family=Libre+Caslon+Display&family=Space+Grotesk:wght@500;600;700&display=swap",
      },
      { rel: "stylesheet", href: appCss },
    ],
  }),
  beforeLoad: async () => {
    await ensureI18nReady();
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const language = activeLanguage();

  return (
    <html lang={language ?? DEFAULT_LANGUAGE}>
      <head>
        <HeadContent />
      </head>
      <body className="bg-[#090909]">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    initTheme();
    initFontGroup();
    void registerServiceWorker();
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider queryClient={queryClient}>
          <LocationProvider>
            <RootApp />
          </LocationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

function RootApp() {
  const { t } = useTranslation("consumer");
  const { user, hydrated, isGuest } = useAuth();
  const { accessKind, hasLoaded, refetch: refetchEntitlements } = useConsumerEntitlements();
  const { data: onboarding, isSuccess: onboardingLoaded } = useConsumerOnboarding();
  const { isDetecting, refreshGpsCoords } = useLocation();
  const router = useRouter();
  const [loginOpen, setLoginOpen] = useState(false);
  const [paywallConfirmed, setPaywallConfirmed] = useState(false);
  const [splashPhase, setSplashPhase] = useState<SplashPrefetchPhase>(() =>
    getSplashPrefetchPhase(),
  );
  const [splashReady, setSplashReady] = useState(false);
  const revalidatingPaywall = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => subscribeSplashPrefetchPhase(setSplashPhase), []);

  useEffect(() => {
    let cancelled = false;
    void getDiscoverySplashPrefetchPromise().then(() => {
      if (!cancelled) setSplashReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showBrandSplash = !splashReady || !hydrated;
  const splashStatusText = useMemo(
    () => t(splashStatusKey(splashPhase)),
    [splashPhase, t],
  );
  const isSquadPage = pathname.includes("/squad/");
  const isResetPasswordPage = pathname === "/reset-password";
  const isWelcomePage = pathname === "/welcome";
  const isVendorPage = pathname.startsWith("/vendor") || pathname === "/for-venues";
  const isAdminPage = isAdminPath(pathname);
  const isMarketingShell =
    pathname === "/" ||
    pathname === "/about" ||
    pathname.startsWith("/user/login") ||
    pathname === "/about";
  const isConsumerApp = !isVendorPage && !isAdminPage && !isResetPasswordPage;
  const isDevQaRoute = pathname.startsWith("/dev/");
  // Account settings must remain reachable when the subscription gate is up
  // (support contact + account deletion live there).
  const isSubscriptionEscapePath = pathname === "/settings";
  const accessExpired =
    isConsumerApp &&
    !!user &&
    !isGuest &&
    hasLoaded &&
    accessKind === "expired" &&
    !isPublicMarketingPath(pathname) &&
    !isWelcomePage;
  const subscriptionExpired =
    accessExpired && !isSubscriptionEscapePath && paywallConfirmed;

  // Revalidate once before showing paywall — covers post-trial-grant stale cache.
  useEffect(() => {
    if (!accessExpired) {
      setPaywallConfirmed(false);
      revalidatingPaywall.current = false;
      return;
    }
    if (paywallConfirmed || revalidatingPaywall.current) return;
    revalidatingPaywall.current = true;
    void loadConsumerEntitlements()
      .then(async (fresh) => {
        if (fresh.accessKind !== "expired") {
          await refetchEntitlements();
          setPaywallConfirmed(false);
          return;
        }
        setPaywallConfirmed(true);
      })
      .finally(() => {
        revalidatingPaywall.current = false;
      });
  }, [accessExpired, paywallConfirmed, refetchEntitlements]);

  useSquadMembershipSync();
  useSquadRoleSync();
  useRejoinRequestSync();
  useSquadInviteSync();

  // Protected consumer app routes require a session — send guests to /user/login.
  useEffect(() => {
    if (!hydrated) return;
    if (user) {
      setLoginOpen(false);
      return;
    }
    if (isPublicMarketingPath(pathname) || isDevQaRoute) {
      setLoginOpen(false);
      return;
    }
    if (isConsumerApp) {
      void router.navigate({ to: "/user/login", replace: true });
    }
  }, [hydrated, user, pathname, isConsumerApp, isDevQaRoute, router]);

  // Gate incomplete welcome → /welcome (DB-backed; existing users backfilled complete).
  // Also leave /welcome once completed (covers stale-cache race right after finish).
  useEffect(() => {
    if (!hydrated || !user || isGuest) return;
    if (!onboardingLoaded || !onboarding) return;
    if (isWelcomePage && onboarding.welcomeCompleted) {
      void router.navigate({ to: "/", replace: true });
      return;
    }
    if (onboarding.welcomeCompleted) return;
    if (
      isWelcomePage ||
      isResetPasswordPage ||
      isVendorPage ||
      isAdminPage ||
      isDevQaRoute ||
      pathname.startsWith("/user/")
    ) {
      return;
    }
    void router.navigate({ to: "/welcome", replace: true });
  }, [
    hydrated,
    user,
    isGuest,
    onboardingLoaded,
    onboarding,
    isWelcomePage,
    isResetPasswordPage,
    isVendorPage,
    isAdminPage,
    isDevQaRoute,
    pathname,
    router,
  ]);

  // Silent GPS refresh only when city already stored AND permission already granted.
  // Never prompt from root — onboarding screen 2 owns the active location ask.
  useEffect(() => {
    if (!hasStoredCity()) return;
    void (async () => {
      try {
        if (!navigator.permissions?.query) return;
        const status = await navigator.permissions.query({
          name: "geolocation" as PermissionName,
        });
        if (status.state === "granted") await refreshGpsCoords();
      } catch {
        /* ignore */
      }
    })();
  }, [refreshGpsCoords]);

  useEffect(() => {
    if (isVendorPage || isAdminPage) return;
    applyStoredLanguage(CONSUMER_LANGUAGE_STORAGE_KEY);
  }, [isVendorPage, isAdminPage]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const timeoutId = window.setTimeout(() => {
      // Warm the Discover map chunk (Leaflet) without blocking first paint.
      void import("@/components/MapPage");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const hideTabBar =
    isVendorPage ||
    isAdminPage ||
    accessExpired ||
    isWelcomePage ||
    pathname === "/about" ||
    pathname.startsWith("/user/") ||
    (!user && isPublicMarketingPath(pathname));

  return (
    <div
      className={cn(
        isAdminPage
          ? "min-h-screen w-full max-w-none bg-gray-50 pb-0"
          : (isMarketingShell && !(user && !isGuest)) ||
              pathname.startsWith("/user/") ||
              isWelcomePage
            ? "relative min-h-screen w-full max-w-none bg-background"
            : isSquadPage
              // Same consumer column as Discovery's base, slightly wider than phone
              // (max-w-xl) so desktop isn't cramped — still a compact app column.
              ? "relative mx-auto h-dvh w-full max-w-xl overflow-hidden bg-background"
              // lg: 1152px; xl (1280+): cap 1440px (arbitrary — no 1440 token in Tailwind config).
              : "relative mx-auto min-h-screen w-full max-w-md bg-background pb-28 sm:max-w-2xl lg:max-w-6xl xl:max-w-[1440px]",
      )}
    >
      {isDetecting && !hideTabBar && !(pathname === "/about" || pathname.startsWith("/user/")) && (
        <p className="pointer-events-none absolute inset-x-0 top-0 z-50 animate-pulse bg-surface/90 px-4 py-1.5 text-center text-[10px] font-medium tracking-wide text-muted-foreground backdrop-blur-sm">
          {LOCATION_DETECTING_LABEL}
        </p>
      )}
      {subscriptionExpired ? (
        <SubscriptionRequiredScreen />
      ) : accessExpired && !isWelcomePage ? (
        <div className="flex min-h-[50vh] items-center justify-center px-4">
          <LoadingBlock />
        </div>
      ) : isConsumerApp && !!user && !isGuest && !hasLoaded && !isWelcomePage ? (
        <div className="flex min-h-[50vh] items-center justify-center px-4">
          <LoadingBlock />
        </div>
      ) : (
        <SoftRouteOutlet />
      )}
      {!hideTabBar && <SoftTabBar />}
      {!hideTabBar && <DiscoveryTour />}
      <Toaster position="top-center" />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      {showBrandSplash ? <BrandSplashOverlay statusText={splashStatusText} /> : null}
    </div>
  );
}
